import { UserPermissions } from "@custom-types/user"
import * as yup from "yup"
import { getSchemaShape } from "@features/run/RunModelForm/validation"
import { GraphDto } from "@custom-types/graphs"
import { format } from "date-fns"
import { ValidationError } from "utils/form"
import { getEligibleWhatIfContractOptions } from "@features/run/RunModelForm/helpers"

export {
  SAMPLING_FIELD_MAX,
  SAMPLING_FIELD_MIN,
  SEED_FIELD_MAX,
  SEED_FIELD_MIN,
} from "@features/run/RunModelForm/validation" // re-export common modules

export const getValidationSchema = (graph: GraphDto, permissions: UserPermissions | undefined) => {
  const contractOptions = getEligibleWhatIfContractOptions(graph.base_graph_family_key, permissions)
  let endDateSchema = yup.date().required(ValidationError.REQUIRED)

  if (graph.start_date !== "rolling") {
    endDateSchema = endDateSchema.min(
      graph.start_date,
      ValidationError.DATE_AFTER(format(new Date(graph.start_date), "yyyy-MM-dd")),
    )
  }

  if (graph.end_date !== "rolling") {
    endDateSchema = endDateSchema.max(
      graph.end_date,
      ValidationError.DATE_BEFORE(format(new Date(graph.end_date), "yyyy-MM-dd")),
    )
  }

  // users can run sampling if What-if
  return yup.object().shape({
    ...getSchemaShape(graph, permissions), // extend RunModelForm validation schema
    description: yup.string(),
    start_date: yup.lazy((value) => (value === "rolling" ? yup.string() : yup.date())),
    end_date: yup.lazy((value) =>
      value === "rolling" ? yup.string().required(ValidationError.REQUIRED) : endDateSchema,
    ),
    contract_key:
      contractOptions.length > 1
        ? yup
            .string()
            .required()
            .oneOf(
              contractOptions.map((x) => x.contract_key),
              ValidationError.SELECT_ONE,
            )
        : yup.string().nullable(),
    scenarios:
      graph.scenarios.length == 0
        ? yup.array().of(yup.string().required().oneOf(graph.scenarios))
        : yup
            .array()
            .of(yup.string().required().oneOf(graph.scenarios))
            .min(1, "Must specify at least one scenario on a graph with scenarios"),
    // Dummy field used to block form submission when async linked graph validation fails
    // (MERGE, PARALLEL, PREVIOUS modes). Errors are set manually via setError('linked_graph_valid').
    linked_graph_valid: yup.boolean().optional(),
    linked_graph_mode: yup
      .string()
      .oneOf(["MERGE", "PARALLEL", "PREVIOUS"])
      .default("MERGE")
      .optional(),
    previous_run_key: yup.array().when("linked_graph_mode", {
      is: "PREVIOUS",
      then: (s) =>
        s.of(
          yup.object().shape({
            graphKey: yup.string(),
            runId: yup.string().required("Run Group ID is required"),
          }),
        ),
      otherwise: (s) =>
        s.of(
          yup.object().shape({
            graphKey: yup.string(),
            runId: yup.string(),
          }),
        ),
    }),
    validate: yup.boolean().default(false),
  })
}
